import {getBaseFrameLayout,getBaseFrameSourceSize,normalizeBaseCustomFrame,type BaseCustomFrameSettings,type BaseFrameRect,type BaseFrameLayout} from '../baseCustomFrame';
import type {BackgroundAdjustments,DeviceShadowAdjustments,DiffuseAdjustments,ScreenOverlayAdjustments} from './composite';
import type {StaticBackgroundComposition} from './staticBackground';
import type {StaticRenderCache} from './StaticRenderCache';
import type {CustomPlacement,ScreenWarpMode} from '../types';
import {createCanvas} from './loadImage';
import {isCanvasMemoryScopeActive,onCanvasScopeEnd} from './canvasMemory';
import {setDynamicZoomLayers} from './dynamicZoomBackground';
import {drawBaseFrameGlass,disposeBaseFrameGlass,type GlassBackgroundRect} from './baseFrameGlass';
import {FrameBlurCache,disposeSmoothFrameBlur} from './smoothFrameBlur';
import {drawFrameRefractionFallback,clearFrameRefractionFallback} from './frameRefractionFallback';

export interface BaseCustomFrameRenderOptions {
  renderSize?:number; baseCustomFrame?:BaseCustomFrameSettings;
  background?:BackgroundAdjustments;staticBackground?:StaticBackgroundComposition & {source:CanvasImageSource};
  separateCameraBackground?:boolean;deviceShadow?:DeviceShadowAdjustments;diffuse?:DiffuseAdjustments;
  customPlacement?:CustomPlacement|null;warpMode?:ScreenWarpMode;staticCache?:StaticRenderCache;screenOverlay?:ScreenOverlayAdjustments;
}
let decorationCache:{key:string;canvas:HTMLCanvasElement}|null=null;
let defaultBackgroundCache:{key:string;canvas:HTMLCanvasElement}|null=null;
const backgroundBlurCache=new FrameBlurCache();
export function disposeBaseCustomFrameResources():void {
  if(decorationCache)decorationCache.canvas.width=decorationCache.canvas.height=1;
  decorationCache=null;defaultBackgroundCache=null;backgroundBlurCache.clear();fallbackFrostCache.clear();clearFrameRefractionFallback();disposeBaseFrameGlass();disposeSmoothFrameBlur();
}

export function framePath(ctx:CanvasRenderingContext2D,rect:BaseFrameRect):void {
  const {x,y,width:w,height:h}=rect,r=Math.max(0,Math.min(rect.radius,w/2,h/2));
  ctx.moveTo(x+r,y);ctx.lineTo(x+w-r,y);ctx.arcTo(x+w,y,x+w,y+r,r);
  ctx.lineTo(x+w,y+h-r);ctx.arcTo(x+w,y+h,x+w-r,y+h,r);
  ctx.lineTo(x+r,y+h);ctx.arcTo(x,y+h,x,y+h-r,r);
  ctx.lineTo(x,y+r);ctx.arcTo(x,y,x+r,y,r);ctx.closePath();
}

function colorsForFrame(settings:BaseCustomFrameSettings,diffuse?:DiffuseAdjustments):[number[],number[]] {
  const convert=(hex:string)=>{
    let rgb=[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)/255);
    if(!diffuse)return rgb;
    const angle=diffuse.hue*Math.PI/180,c=Math.cos(angle),s=Math.sin(angle);
    const [r,g,b]=rgb;
    rgb=[(.213+.787*c-.213*s)*r+(.715-.715*c-.715*s)*g+(.072-.072*c+.928*s)*b,
      (.213-.213*c+.143*s)*r+(.715+.285*c+.140*s)*g+(.072-.072*c-.283*s)*b,
      (.213-.213*c-.787*s)*r+(.715-.715*c+.715*s)*g+(.072+.928*c+.072*s)*b];
    const luma=rgb[0]*.2126+rgb[1]*.7152+rgb[2]*.0722,saturation=diffuse.monochrome?0:diffuse.saturation/100;
    return rgb.map(v=>Math.max(0,Math.min(1,((luma+(v-luma)*saturation)*diffuse.brightness/100-.5)*diffuse.contrast/100+.5)));
  };
  return [convert(settings.color),convert(settings.fill==='gradient'?settings.color2:settings.color)];
}

function fillForFrame(ctx:CanvasRenderingContext2D,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,colors:[number[],number[]]) {
  const css=(rgb:number[])=>`rgb(${rgb.map(v=>Math.round(v*255)).join(',')})`;
  if(settings.fill==='solid')return css(colors[0]);
  const r=layout.outer,angle=settings.angle*Math.PI/180,dx=Math.cos(angle),dy=Math.sin(angle),length=(Math.abs(dx)*r.width+Math.abs(dy)*r.height)/2;
  const gradient=ctx.createLinearGradient(r.x+r.width/2-dx*length,r.y+r.height/2-dy*length,r.x+r.width/2+dx*length,r.y+r.height/2+dy*length);
  gradient.addColorStop(0,css(colors[0]));gradient.addColorStop(1,css(colors[1]));return gradient;
}

function drawRing(ctx:CanvasRenderingContext2D,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,colors:[number[],number[]],shine=1) {
  if(layout.border<=0)return;
  ctx.save();ctx.beginPath();framePath(ctx,layout.outer);framePath(ctx,layout.inner);ctx.clip('evenodd');
  ctx.globalAlpha=settings.opacity/100;ctx.fillStyle=fillForFrame(ctx,layout,settings,colors);
  ctx.fillRect(layout.outer.x,layout.outer.y,layout.outer.width,layout.outer.height);
  ctx.globalAlpha*=.16*shine;ctx.strokeStyle='#ffffff';ctx.lineWidth=Math.max(1,layout.border*.05);ctx.beginPath();framePath(ctx,layout.outer);ctx.stroke();ctx.restore();
}

function drawGlass(ctx:CanvasRenderingContext2D,background:CanvasImageSource,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,colors:[number[],number[]],scale:number,crop?:GlassBackgroundRect,shine=1,revision='') {
  if(layout.border<=0)return;
  if(drawBaseFrameGlass(ctx,background,layout,settings,colors,scale,crop,shine,revision))return;
  // Preserve the outer-only bevel even if the browser cannot use WebGL.
  ctx.save();ctx.beginPath();framePath(ctx,layout.outer);framePath(ctx,layout.inner);ctx.clip('evenodd');
  const w=Number((background as any).width)||ctx.canvas.width,h=Number((background as any).height)||ctx.canvas.height;
  const source=crop||{x:0,y:0,width:w,height:h};
  const frosted=fallbackFrostCache.get(background,settings.blur*scale*source.width/ctx.canvas.width,settings.blur*scale*source.height/ctx.canvas.height,revision);
  const fx=Number((frosted as any).width)/w,fy=Number((frosted as any).height)/h;
  drawFrameRefractionFallback(ctx,frosted,layout,settings.refraction,scale,{x:source.x*fx,y:source.y*fy,width:source.width*fx,height:source.height*fy},revision);
  ctx.filter='none';ctx.globalAlpha=settings.opacity/100;ctx.fillStyle=fillForFrame(ctx,layout,settings,colors);ctx.fillRect(0,0,ctx.canvas.width,ctx.canvas.height);
  ctx.globalAlpha=Math.min(.24,.12*shine);ctx.strokeStyle='#fff';ctx.lineWidth=Math.max(1,scale);ctx.beginPath();framePath(ctx,layout.outer);ctx.stroke();ctx.restore();
}
const fallbackFrostCache=new FrameBlurCache();

function buildDecoration(size:number,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,options:BaseCustomFrameRenderOptions,colors:[number[],number[]]):HTMLCanvasElement {
  const canvas=createCanvas(size,size),ctx=canvas.getContext('2d')!;
  const shadow=options.deviceShadow;
  if(shadow?.enabled){
    const styles={soft:[.022,.022,.16],close:[.012,.011,.22],floating:[.038,.034,.14],long:[.058,.024,.17]};
    const [offset,blur,opacity]=styles[shadow.preset]||styles.soft,angle=shadow.angle*Math.PI/180;
    const rgb=/^#[\da-f]{6}$/i.test(shadow.color)?shadow.color:'#000000';
    const frameScale=settings.scale/100;
    ctx.shadowColor=rgb;ctx.shadowBlur=size*blur*frameScale;ctx.shadowOffsetX=Math.cos(angle)*size*offset*frameScale;ctx.shadowOffsetY=Math.sin(angle)*size*offset*frameScale;
    ctx.globalAlpha=opacity*Math.max(0,Math.min(100,shadow.opacity))/100;ctx.fillStyle=rgb;ctx.beginPath();framePath(ctx,layout.outer);ctx.fill();
    ctx.shadowColor='transparent';ctx.shadowBlur=ctx.shadowOffsetX=ctx.shadowOffsetY=0;ctx.globalAlpha=1;
    ctx.globalCompositeOperation='destination-out';ctx.beginPath();framePath(ctx,layout.outer);ctx.fill();ctx.globalCompositeOperation='source-over';
  }
  if(!settings.glass)drawRing(ctx,layout,settings,colors,Math.max(0,Math.min(2,(options.screenOverlay?.opacity??50)/50)));
  return canvas;
}

function frameDecoration(size:number,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,options:BaseCustomFrameRenderOptions,colors:[number[],number[]]) {
  const key=JSON.stringify([size,layout,settings,options.deviceShadow,options.screenOverlay,colors]);
  if(options.staticCache)return options.staticCache.canvas('base-frame-decoration',[key],()=>buildDecoration(size,layout,settings,options,colors));
  if(!isCanvasMemoryScopeActive()&&decorationCache?.key===key)return decorationCache.canvas;
  const canvas=buildDecoration(size,layout,settings,options,colors);
  if(!isCanvasMemoryScopeActive()){
    if(decorationCache)decorationCache.canvas.width=decorationCache.canvas.height=1;
    decorationCache={key,canvas};
  }
  return canvas;
}

function prepareBackground(size:number,options:BaseCustomFrameRenderOptions):CanvasImageSource {
  if(options.staticBackground&&options.staticBackground.mode!=='none'&&!options.background?.transparent)return options.staticBackground.source;
  const key=JSON.stringify([size,options.background]);
  if(defaultBackgroundCache?.key===key)return defaultBackgroundCache.canvas;
  const canvas=document.createElement('canvas');canvas.width=canvas.height=size;const ctx=canvas.getContext('2d')!;
  defaultBackgroundCache={key,canvas};
  if(options.background?.transparent)return canvas;
  const gradient=ctx.createLinearGradient(0,0,size,size),b=options.background;
  if(b?.mode==='tint'){gradient.addColorStop(0,b.midColor);gradient.addColorStop(1,b.midColor);}
  else if(b?.mode==='tritone'){gradient.addColorStop(0,b.shadowColor);gradient.addColorStop(.5,b.midColor);gradient.addColorStop(1,b.highlightColor);}
  else {gradient.addColorStop(0,'#d9e8f8');gradient.addColorStop(1,'#e9daee');}
  ctx.fillStyle=gradient;ctx.fillRect(0,0,size,size);return canvas;
}

function drawFootage(ctx:CanvasRenderingContext2D,source:CanvasImageSource|null,rect:BaseFrameRect,options:BaseCustomFrameRenderOptions){
  ctx.save();ctx.beginPath();framePath(ctx,rect);ctx.clip();
  if(source){
    const natural=getBaseFrameSourceSize(source),custom=options.warpMode==='custom'?options.customPlacement:null;
    const scale=custom?Math.max(.05,Math.min(6,Number(custom.scale)||1)):1;
    const cover=Math.max(rect.width/natural.width,rect.height/natural.height)*scale;
    const w=natural.width*cover,h=natural.height*cover;
    ctx.drawImage(source,rect.x+(rect.width-w)/2+(custom?.offsetX||0)*rect.width,rect.y+(rect.height-h)/2+(custom?.offsetY||0)*rect.height,w,h);
  }else{
    ctx.fillStyle='#f7f9fe';ctx.fillRect(rect.x,rect.y,rect.width,rect.height);
    ctx.fillStyle='#e2e8f6';ctx.fillRect(rect.x+rect.width*.08,rect.y+rect.height*.14,rect.width*.42,rect.height*.055);
    ctx.fillRect(rect.x+rect.width*.08,rect.y+rect.height*.26,rect.width*.32,rect.height*.025);
    ctx.fillRect(rect.x+rect.width*.57,rect.y+rect.height*.14,rect.width*.35,rect.height*.7);
    ctx.fillStyle='#5571dd';ctx.fillRect(rect.x+rect.width*.08,rect.y+rect.height*.65,rect.width*.22,rect.height*.1);
  }
  ctx.restore();
}

export function renderBaseCustomFrame(source:CanvasImageSource|null,options:BaseCustomFrameRenderOptions):HTMLCanvasElement {
  if(isCanvasMemoryScopeActive())onCanvasScopeEnd(disposeBaseCustomFrameResources);
  const size=Math.max(1,Math.round(options.renderSize||3840)),settings=normalizeBaseCustomFrame(options.baseCustomFrame);
  const natural=getBaseFrameSourceSize(source),layout=getBaseFrameLayout(natural.width/natural.height,size,settings),colors=colorsForFrame(settings,options.diffuse);
  const rawBackground=prepareBackground(size,options),composition=options.staticBackground;
  // Sequence renderers intentionally reuse their canvas; include the authored
  // composition in the revision to avoid freezing an animated gradient.
  const revision=composition?JSON.stringify([composition.mode,composition.colors,composition.axisStart,composition.axisEnd,composition.stopPositions,composition.angle,composition.meshPoints,composition.meshMix,composition.meshDistortion,composition.meshGrain,composition.meshFlowCenter,composition.imageFit,composition.imagePlacement]):'';
  const background=backgroundBlurCache.get(rawBackground,settings.backgroundBlur*Number((rawBackground as any).width)/1920,settings.backgroundBlur*Number((rawBackground as any).height)/1920,revision);
  const master=createCanvas(size,size),ctx=master.getContext('2d')!;
  ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
  const shine=Math.max(0,Math.min(2,(options.screenOverlay?.opacity??50)/50));
  const separate=options.separateCameraBackground;
  if(!separate)ctx.drawImage(background,0,0,size,size);
  ctx.drawImage(frameDecoration(size,layout,settings,options,colors),0,0);
  if(settings.glass&&!separate)drawGlass(ctx,background,layout,settings,colors,size/1920*settings.scale/100,undefined,shine,revision);
  drawFootage(ctx,source,layout.inner,options);
  if(separate){
    setDynamicZoomLayers(master,{background,beforeForeground:settings.glass?(target,foregroundRect,backgroundRect)=>{
      const sx=target.canvas.width/foregroundRect.width,sy=target.canvas.height/foregroundRect.height,s=Math.min(sx,sy);
      const transform=(r:BaseFrameRect):BaseFrameRect=>({x:(r.x-foregroundRect.x)*sx,y:(r.y-foregroundRect.y)*sy,width:r.width*sx,height:r.height*sy,radius:r.radius*s});
      drawGlass(target,background,{outer:transform(layout.outer),inner:transform(layout.inner),border:layout.border*s},settings,colors,size/1920*settings.scale/100*s,backgroundRect,shine,revision);
    }:undefined});
  }
  return master;
}
